/**
 * Shared job-submission transport, used by both the NetWAS form
 * (components/gwas/GwasForm) and DeepSEA
 * (actions/DeepSEAActions.fetchNewDeepSEAJob).
 *
 * POSTs a FormData body and normalizes the outcome into one shape:
 * `{ ok, status, body, errors }`. For failures, `errors` is a ready-to-render
 * object of the server's messages (DRF `detail`, field errors,
 * `non_field_errors`) plus a synthetic `statusError`/`fetchError` when the
 * server or network gives us nothing usable. Deciding success stays with the
 * caller (it knows whether it expects `id` or `job_id`).
 */

export interface HttpResponse {
  ok: boolean;
  status: number;
  statusText: string;
  json(): Promise<unknown>;
}

export type FetchLike = (
  url: string,
  init: { method: string; body: FormData },
) => Promise<HttpResponse>;

export interface JobSubmissionResult {
  ok: boolean;
  status: number;
  body: Record<string, unknown>;
  errors: Record<string, unknown>;
}

const DEFAULT_FETCH_ERROR = 'There was an unknown error creating the job.';

// A value carries a renderable message if it is a non-blank string or an
// array containing at least one non-blank string (DRF field errors /
// non_field_errors). Blank strings are treated as "no message" so they never
// suppress the statusError fallback into an empty error alert.
function hasRenderableMessage(errors: Record<string, unknown>): boolean {
  return Object.values(errors).some(
    value =>
      (typeof value === 'string' && value.trim() !== '') ||
      (Array.isArray(value) &&
        value.some(item => typeof item === 'string' && item.trim() !== '')),
  );
}

export async function submitJobForm(
  url: string,
  formData: FormData,
  fetchFn: FetchLike,
): Promise<JobSubmissionResult> {
  let response: HttpResponse;
  try {
    response = await fetchFn(url, { method: 'POST', body: formData });
  } catch {
    // The request never completed (offline, DNS, CORS, or abort), so there is
    // no status to report.
    return {
      ok: false,
      status: 0,
      body: {},
      errors: { fetchError: DEFAULT_FETCH_ERROR },
    };
  }

  const { ok, status, statusText } = response;
  let body: Record<string, unknown> = {};
  try {
    const parsed = await response.json();
    // Objects only: an array would spread into numeric keys, and a bare
    // string/number/null is not an error map.
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      body = parsed as Record<string, unknown>;
    }
  } catch {
    // A missing or non-JSON body (500 HTML page, empty 4xx, or mid-read abort)
    // leaves the body empty; the real status is preserved and surfaced below.
  }

  let errors: Record<string, unknown> = {};
  if (!ok) {
    errors = { ...body };
    // Guarantee a displayable message: if the body carried nothing a UI can
    // render (empty, or only structured values like `stats`), fall back to
    // the HTTP status so a failure is never silent.
    if (!hasRenderableMessage(errors)) {
      errors = { statusError: statusText || `Server error (${status})` };
    }
  }
  return { ok, status, body, errors };
}
